import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Icon,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import { MenuOutlined, PersonOutlineOutlined } from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute, hasValidConsumerSubscription } from "src/services";
import StanzaLogo from "src/assets/svg/stanza-logo.js";
import StanzaSmallLogo from "src/assets/svg/stanza-small-icon.js";
import { SideMenu } from "src/components/blocks/SideMenu.js";
import OfferPill, {
  shouldShowOfferPill,
} from "src/components/upgrades/OfferPill.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { TOPBAR_HEIGHT } from "src/styles/layout.js";
import { CTA_SUPPRESSED_PATH_PREFIXES, isCatalogPath } from "./sidebarVisibility.js";

/**
 * Sticky top bar for every page. Catalog pages get the centered search field;
 * elsewhere the compact search action opens the dedicated search experience.
 *
 * @param {{ onMenuClick?: () => void, variant?: "light"|"dark" }} props
 */
const DiscoveryTopbar = ({ onMenuClick, variant = "light" }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMediumScreen } = useDimensions();
  const [term, setTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const inputRef = useRef(null);

  const isDark = variant === "dark";
  const iconColor = isDark ? "white" : "icon.primary";
  const loggedIn = Boolean(user);
  const ctaSuppressed = CTA_SUPPRESSED_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );
  const isCatalog = isCatalogPath(location.pathname);
  // The countdown pill takes the "Go Plus" slot when it has something to
  // show; the button stays as the fallback when no countdown is active.
  const pillVisible = loggedIn && !ctaSuppressed && shouldShowOfferPill(user);
  const showGoPlus =
    !ctaSuppressed && !hasValidConsumerSubscription(user) && !pillVisible;
  // Signed-in mobile swaps the wordmark for the small tile to keep the row compact.
  const showSmallLogo = isMediumScreen && loggedIn;
  // The search page has its own bar; the topbar drops its search entirely
  // there. Exact-prefix match so /:handle pages like /searchlight keep theirs.
  const onSearchPage =
    location.pathname === "/search" || location.pathname.startsWith("/search/");
  // Desktop always carries the full bar; mobile expands it in place on tap.
  const searchExpanded = !onSearchPage && (!isMediumScreen || searchOpen);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  // The bar is a launcher: the query moves to the search page on submit, so a
  // typed term doesn't linger once the user lands anywhere else. The side menu
  // closes here too — the topbar persists across route changes, so a menu item
  // that navigates would otherwise leave the drawer open on the next page.
  useEffect(() => {
    setTerm("");
    setSearchOpen(false);
    setSideMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = () => {
    if (!term.trim()) return;
    navigate(getPageRoute("chat", "SEARCH"), { state: { term } });
  };

  const outlinedSx = {
    display: "inline",
    flexShrink: 0,
    whiteSpace: "nowrap",
    color: isDark ? "white" : "black",
    borderColor: isDark ? "white" : "black",
    "&:hover": {
      borderColor: isDark ? "white" : "black",
      backgroundColor: isDark ? "overlay.white10" : "overlay.black04",
    },
  };

  return (
    <>
      <Box
        component="header"
        data-testid="discovery-topbar"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          height: `${TOPBAR_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, md: 1.5 },
          px: { xs: 1, md: 2.5 },
          backgroundColor: isDark ? "black" : "paper.primary",
          borderBottom: (theme) =>
            `1px solid ${isDark ? theme.palette.overlay.white15 : theme.palette.border.tertiary}`,
        }}
      >
        {!searchOpen && (
          <>
            <Tooltip title="Navigation">
              <IconButton
                onClick={onMenuClick}
                aria-label="Toggle navigation"
                data-testid="discovery-menu-toggle"
                size="small"
              >
                <MenuOutlined fontSize="medium" sx={{ color: iconColor }} />
              </IconButton>
            </Tooltip>

            <Box
              component={RouterLink}
              to={getPageRoute("home", "HOME")}
              aria-label="Stanza home"
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                // Match the desktop hamburger-to-wordmark spacing despite the
                // tighter mobile flex gap.
                ml: { xs: 1, md: 0 },
                ...(showSmallLogo
                  ? // The two-tone tile can't use path fills; invert swaps it for dark topbars.
                    { filter: isDark ? "invert(1)" : undefined }
                  : // Invert the wordmark on dark topbars so it isn't a black-on-black blob.
                    { "& svg, & svg path": { fill: isDark ? "white" : "black" } }),
              }}
            >
              {showSmallLogo ? <StanzaSmallLogo /> : <StanzaLogo />}
            </Box>
          </>
        )}

        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
          {searchExpanded ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                maxWidth: "640px",
                height: "40px",
                borderRadius: "20px",
                overflow: "hidden",
                // Catalog pages carry the bold bar; timeline pages get a quiet
                // grey outline that darkens to black on focus (typing).
                border: isCatalog ? "1.5px solid" : "1px solid",
                borderColor: isDark
                  ? "common.white"
                  : isCatalog
                    ? "common.black"
                    : "border.tertiary",
                "&:focus-within": {
                  borderColor: isDark ? "common.white" : "common.black",
                },
              }}
            >
              <InputBase
                inputRef={inputRef}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
                onBlur={() => setSearchOpen(false)}
                placeholder="Search for calendars"
                inputProps={{
                  "aria-label": "Search for calendars",
                  "data-testid": "discovery-search-input",
                }}
                sx={{
                  flex: 1,
                  px: 2,
                  fontSize: "0.9rem",
                  color: isDark ? "white" : "inherit",
                  ...(isDark && {
                    "& input::placeholder": { color: "rgba(255,255,255,0.6)", opacity: 1 },
                  }),
                }}
              />
              <IconButton
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleSearch}
                aria-label="Search"
                sx={{ mx: "2px", width: "36px", height: "36px" }}
              >
                <Icon baseClassName="material-symbols-outlined" sx={{ color: iconColor }}>
                  search
                </Icon>
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
        </Box>

        {!searchOpen && (
          <>
            {pillVisible && <OfferPill />}

            {showGoPlus && (
              <Button
                variant="outlined"
                size="shrinked"
                data-testid="topbar-go-plus"
                onClick={() =>
                  navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                    state: { source: loggedIn ? "nav_button" : "nav_button_signed_out" },
                  })
                }
                sx={outlinedSx}
              >
                Go Plus
              </Button>
            )}

            {!searchExpanded && !onSearchPage && (
              <Tooltip title="Search">
                {/* Mobile only: expands the bar in place. */}
                <IconButton
                  onClick={() => setSearchOpen(true)}
                  aria-label="Open search"
                  size="small"
                  sx={{ display: { xs: "inline-flex", md: "none" }, ml: 1 }}
                >
                  <Icon baseClassName="material-symbols-outlined" fontSize="medium" sx={{ color: iconColor }}>
                    search
                  </Icon>
                </IconButton>
              </Tooltip>
            )}

            {loggedIn ? (
              <>
                <Tooltip title="Open Settings">
                  <IconButton
                    data-testid="home-user-menu"
                    onClick={() => setSideMenuOpen(true)}
                    size="small"
                    sx={{ ml: { xs: 1, md: 0 } }}
                  >
                    <PersonOutlineOutlined fontSize="medium" sx={{ color: iconColor }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to={getPageRoute("auth", "SIGNIN")}
                  variant="text"
                  sx={{
                    display: { xs: "none", md: "inline" },
                    flexShrink: 0,
                    color: isDark ? "white" : "text.primary",
                  }}
                >
                  Sign In
                </Button>
                {/* Mobile stand-in for the desktop Sign In button. */}
                <Tooltip title="Sign In">
                  <IconButton
                    data-testid="home-user-menu"
                    onClick={() => navigate(getPageRoute("auth", "SIGNIN"))}
                    size="small"
                    sx={{ ml: 1, display: { xs: "inline-flex", md: "none" } }}
                  >
                    <PersonOutlineOutlined fontSize="medium" sx={{ color: iconColor }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        )}
      </Box>

      <SideMenu open={sideMenuOpen} onClose={() => setSideMenuOpen(false)} user={user} />
    </>
  );
};

export default DiscoveryTopbar;
